import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Building, Calendar, ChevronRight, RotateCcw, XCircle } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ExpiringSubscription } from '@/services/dashboard';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';

interface ExpiringSubscriptionsWidgetProps {
  subscriptions: ExpiringSubscription[];
  loading?: boolean;
}

const ExpiringSubscriptionsWidget: React.FC<ExpiringSubscriptionsWidgetProps> = ({
  subscriptions,
  loading = false
}) => {
  const navigate = useNavigate();

  const getCustomerName = (subscription: ExpiringSubscription) => {
    const customer = subscription.subscription_customers?.[0]?.customer;
    return customer?.name || 'Cliente não identificado';
  };

  const getPlanName = (subscription: ExpiringSubscription) => {
    const item = subscription.items?.[0];
    return item?.subscription_plan?.name || 'Plano não identificado';
  };

  const getTotalValue = (subscription: ExpiringSubscription) => {
    return subscription.items?.reduce((total, item) => {
      return total + (item.unit_price * item.quantity);
    }, 0) || 0;
  };

  const getDaysUntilExpiration = (endDate: string) => {
    return differenceInDays(parseISO(endDate), new Date());
  };

  const getUrgencyBadge = (daysLeft: number, autoRenew: boolean) => {
    if (daysLeft <= 7) {
      return <Badge variant="destructive">Crítico</Badge>;
    } else if (daysLeft <= 14) {
      return <Badge variant="secondary" className="bg-orange-100 text-orange-800">Urgente</Badge>;
    } else if (daysLeft <= 30) {
      return <Badge variant="outline" className="border-yellow-300 text-yellow-700">Atenção</Badge>;
    }
    return <Badge variant="outline">Normal</Badge>;
  };

  const getRenewalBadge = (autoRenew: boolean) => {
    return autoRenew ? (
      <Badge variant="default" className="bg-green-100 text-green-800">
        <RotateCcw className="h-3 w-3 mr-1" />
        Auto Renovação
      </Badge>
    ) : (
      <Badge variant="outline" className="border-red-300 text-red-700">
        <XCircle className="h-3 w-3 mr-1" />
        Renovação Manual
      </Badge>
    );
  };

  // Separar assinaturas por tipo de renovação
  const withAutoRenew = subscriptions.filter(sub => sub.auto_renew);
  const withoutAutoRenew = subscriptions.filter(sub => !sub.auto_renew);

  if (loading) {
    return (
      <Card className="border-0 shadow-sm h-full">
        <CardHeader className="pb-1 pt-3 px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-extrabold">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            Vencendo em 60 dias
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 h-[calc(100%-55px)] flex flex-col">
          <div className="space-y-2 flex-1">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm h-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-4">
        <CardTitle className="flex items-center gap-2 text-sm font-extrabold">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          Vencendo em 60 dias
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/subscriptions')}
          className="h-7 px-2"
        >
          Ver todas
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0 h-[calc(100%-55px)] flex flex-col">
        {subscriptions.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Nenhuma assinatura vencendo</p>
          </div>
        ) : (
          <div className="space-y-2 flex-1 overflow-y-auto">
            {subscriptions.slice(0, 6).map((subscription) => {
              const daysLeft = getDaysUntilExpiration(subscription.end_date);
              return (
                <div
                  key={subscription.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors min-h-[80px]"
                  onClick={() => navigate(`/subscriptions/${subscription.id}`)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-extrabold truncate">
                        {getCustomerName(subscription)}
                      </p>
                      {getUrgencyBadge(daysLeft, subscription.auto_renew)}
                      {getRenewalBadge(subscription.auto_renew)}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                      <span className="font-extrabold">{getPlanName(subscription)}</span>
                      <span>•</span>
                      <span className="font-extrabold">
                        {formatCurrency(getTotalValue(subscription))}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Vence em {daysLeft} dias ({format(parseISO(subscription.end_date), "dd/MM/yyyy", { locale: ptBR })})
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              );
            })}
          </div>
        )}
        
        {subscriptions.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Com renovação:</span>
                <span className="font-extrabold text-green-600">{withAutoRenew.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Sem renovação:</span>
                <span className="font-extrabold text-red-600">{withoutAutoRenew.length}</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExpiringSubscriptionsWidget;
